/* @refresh skip */
import React from 'react';

import { cn } from '../../lib/utils';

type DivProps = React.HTMLAttributes<HTMLDivElement>;
type ParagraphProps = React.HTMLAttributes<HTMLParagraphElement>;
type HeadingProps = React.HTMLAttributes<HTMLHeadingElement>;

type ForwardRefComponent<TElement extends HTMLElement, TProps> = React.ForwardRefExoticComponent<
  TProps & React.RefAttributes<TElement>
>;

const createForwardRef = <TElement extends HTMLElement, TProps extends object>(
  tag: keyof JSX.IntrinsicElements,
  baseClassName: string,
): ForwardRefComponent<TElement, TProps> =>
  React.forwardRef<TElement, TProps & { className?: string }>(
    ({ className, ...props }, ref) =>
      React.createElement(tag, {
        ref,
        className: cn(baseClassName, className),
        ...props,
      }),
  );

export const Card = React.forwardRef<HTMLDivElement, DivProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn('rounded-lg border bg-card text-card-foreground shadow-sm', className)}
      {...props}
    />
  ),
);
Card.displayName = 'Card';

export const CardHeader = createForwardRef<HTMLDivElement, DivProps>('div', 'flex flex-col space-y-1.5 p-6');
CardHeader.displayName = 'CardHeader';

export const CardTitle = createForwardRef<HTMLHeadingElement, HeadingProps>(
  'h3',
  'text-2xl font-semibold leading-none tracking-tight',
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = createForwardRef<HTMLParagraphElement, ParagraphProps>(
  'p',
  'text-sm text-muted-foreground',
);
CardDescription.displayName = 'CardDescription';

export const CardContent = createForwardRef<HTMLDivElement, DivProps>('div', 'p-6 pt-0');
CardContent.displayName = 'CardContent';

export const CardFooter = createForwardRef<HTMLDivElement, DivProps>('div', 'flex items-center p-6 pt-0');
CardFooter.displayName = 'CardFooter';
